import type { TableRootProps, TableScrollAreaProps } from '@chakra-ui/react';
import { Box, createSlotRecipeContext, Flex, Table as ChakraTable } from '@chakra-ui/react';
import * as React from 'react';

import { Spinner } from '../spinner';
import {
  dataTableBorderColor,
  dataTableDefaultSize,
  dataTableLoadingOverlayBg,
  dataTableLoadingSpinnerColor,
  dataTableOutlineBorderRadius,
  type DataTableSize,
} from './data-table.kds';

const { withProvider, withContext } = createSlotRecipeContext({ key: 'dataTable' });

export type DataTableRootProps = Omit<TableRootProps, 'size' | 'variant'> & {
  size?: DataTableSize;
};

const DataTableRootInner = withProvider(ChakraTable.Root, 'root') as typeof ChakraTable.Root;

/**
 * When ScrollArea's inline-header slot has DOM content, drop the table's top outline so it
 * shares the banner border. Driven by `:has`/`:empty` so null-returning banners do not join.
 */
const scrollAreaInlineHeaderJoinCss = {
  '&:has([data-inline-header]:not(:empty)) [data-table-root]': {
    borderTopWidth: '0',
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    // Recipe still rounds first thead cells for standalone tables — clear them so the seam is flush.
    '& thead tr:first-of-type th:first-of-type': { borderTopLeftRadius: '0' },
    '& thead tr:first-of-type th:last-of-type': { borderTopRightRadius: '0' },
  },
} as const;

const DataTableRoot = React.forwardRef<HTMLTableElement, DataTableRootProps>(function DataTableRoot(props, ref) {
  const { size = dataTableDefaultSize, css, ...rest } = props;

  return <DataTableRootInner ref={ref} size={size} data-table-root="" css={css} {...rest} />;
});

const DataTableHeader = withContext(ChakraTable.Header, 'header') as typeof ChakraTable.Header;
const DataTableBody = withContext(ChakraTable.Body, 'body') as typeof ChakraTable.Body;
const DataTableRow = withContext(ChakraTable.Row, 'row') as typeof ChakraTable.Row;
const DataTableColumnHeader = withContext(ChakraTable.ColumnHeader, 'columnHeader') as typeof ChakraTable.ColumnHeader;
const DataTableCell = withContext(ChakraTable.Cell, 'cell') as typeof ChakraTable.Cell;
const DataTableFooter = withContext(ChakraTable.Footer, 'footer') as typeof ChakraTable.Footer;
const DataTableCaption = withContext(ChakraTable.Caption, 'caption') as typeof ChakraTable.Caption;

export interface DataTableScrollAreaProps extends TableScrollAreaProps {
  /**
   * Banner above the table, inside the shared outline.
   * Null-returning children are fine: an empty slot is hidden and the table keeps its top border.
   * Pass `null`/`undefined`/`false` to skip mounting the slot entirely.
   */
  inlineHeader?: React.ReactNode;
}

const DataTableScrollArea = React.forwardRef<HTMLDivElement, DataTableScrollAreaProps>(
  function DataTableScrollArea(props, ref) {
    const { inlineHeader, width = 'full', whiteSpace = 'nowrap', overflowX = 'auto', position = 'relative', css, children, ...rest } =
      props;

    const mountInlineHeaderSlot = inlineHeader != null && inlineHeader !== false;

    return (
      <ChakraTable.ScrollArea
        ref={ref}
        width={width}
        whiteSpace={whiteSpace}
        overflowX={overflowX}
        position={position}
        css={mountInlineHeaderSlot ? [scrollAreaInlineHeaderJoinCss, css] : css}
        {...rest}
      >
        {mountInlineHeaderSlot ? (
          <Box
            data-inline-header=""
            data-testid="data-table-inline-header"
            borderWidth="1px"
            borderBottomWidth="0"
            borderColor={dataTableBorderColor}
            borderTopRadius={dataTableOutlineBorderRadius}
            overflow="hidden"
            whiteSpace="normal"
            position="sticky"
            left={0}
            // Hide when the banner returned null so the table keeps a full outline.
            css={{ '&:empty': { display: 'none' } }}
          >
            {inlineHeader}
          </Box>
        ) : null}
        {children}
      </ChakraTable.ScrollArea>
    );
  }
);

export interface DataTableLoadingOverlayProps {
  visible?: boolean;
}

const DataTableLoadingOverlay = ({ visible = false }: Readonly<DataTableLoadingOverlayProps>) => {
  if (!visible) {
    return null;
  }

  return (
    <Flex
      data-testid="data-table-loading-overlay"
      pos="absolute"
      inset={0}
      bg={dataTableLoadingOverlayBg}
      align="center"
      justify="center"
      zIndex="100"
    >
      <Spinner size="lg" color={dataTableLoadingSpinnerColor} />
    </Flex>
  );
};

import { DataTableComposed } from './data-table-composed';
import { DataTableSkeleton } from './data-table-skeleton';
import { DataTableSortIcon } from './data-table-sort-icon';

type DataTableNamespace = typeof DataTableComposed & {
  Root: typeof DataTableRoot;
  ScrollArea: typeof DataTableScrollArea;
  Header: typeof DataTableHeader;
  Body: typeof DataTableBody;
  Row: typeof DataTableRow;
  ColumnHeader: typeof DataTableColumnHeader;
  Cell: typeof DataTableCell;
  Footer: typeof DataTableFooter;
  Caption: typeof DataTableCaption;
  Column: typeof ChakraTable.Column;
  ColumnGroup: typeof ChakraTable.ColumnGroup;
  LoadingOverlay: typeof DataTableLoadingOverlay;
  Skeleton: typeof DataTableSkeleton;
  SortIcon: typeof DataTableSortIcon;
};

export const DataTable = DataTableComposed as DataTableNamespace;
DataTable.Root = DataTableRoot;
DataTable.ScrollArea = DataTableScrollArea;
DataTable.Header = DataTableHeader;
DataTable.Body = DataTableBody;
DataTable.Row = DataTableRow;
DataTable.ColumnHeader = DataTableColumnHeader;
DataTable.Cell = DataTableCell;
DataTable.Footer = DataTableFooter;
DataTable.Caption = DataTableCaption;
DataTable.Column = ChakraTable.Column;
DataTable.ColumnGroup = ChakraTable.ColumnGroup;
DataTable.LoadingOverlay = DataTableLoadingOverlay;
DataTable.Skeleton = DataTableSkeleton;
DataTable.SortIcon = DataTableSortIcon;
